/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  createErrorStateResource,
  createStateResource,
  doInIntervall,
  hasStateResourceError,
  isNotLoading,
  isNotNil,
  isUnprocessableEntity,
  pollWhile,
  StateResource,
} from '@common/tech-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getUrl, hasLink } from '@ngxp/rest';
import { isNil } from 'lodash-es';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { CommandActions, UpdateCreateCommandProps } from './+state/command.actions';
import { CommandSelectors } from './+state/command.selectors';
import { CommandLinkRel } from './command.linkrel';
import { CommandResource, CreateCommandActionProps } from './command.model';
import { CommandRepository } from './command.repository';
import { isPending } from './command.util';

@Injectable({ providedIn: 'root' })
export class CommandService {
  private readonly repository = inject(CommandRepository);
  private readonly store = inject(Store);

  public createCommand(createCommandActionProps: CreateCommandActionProps): Observable<StateResource<CommandResource>> {
    this.store.dispatch(CommandActions.createCommand(createCommandActionProps));
    return this.getAndPoll(createCommandActionProps);
  }

  getAndPoll(createCommandActionProps: CreateCommandActionProps): Observable<StateResource<CommandResource>> {
    return this._getCommand(createCommandActionProps).pipe(
      switchMap(
        (command: StateResource<CommandResource>): Observable<StateResource<CommandResource>> =>
          this.getOrPollPending(createCommandActionProps, command),
      ),
    );
  }

  getOrPollPending(
    createCommandActionProps: CreateCommandActionProps,
    command: StateResource<CommandResource>,
  ): Observable<StateResource<CommandResource>> {
    return isNil(command.resource) || !isPending(command.resource) ?
        of(command)
      : this.pollUntilNotPendingThenDispatchUpdate(createCommandActionProps, command);
  }

  pollUntilNotPendingThenDispatchUpdate(
    createCommandActionProps: CreateCommandActionProps,
    command: StateResource<CommandResource>,
  ): Observable<StateResource<CommandResource>> {
    return this.pollUntilNotPending(command.resource).pipe(
      catchError((errorResponse: HttpErrorResponse) => of(createErrorStateResource(errorResponse.error))),
      tap((update: StateResource<CommandResource>): void => this.dispatchUpdateIfNotPending(createCommandActionProps, update)),
      filter((command: StateResource<CommandResource>) => !hasStateResourceError(command)),
    );
  }

  pollUntilNotPending(command: CommandResource): Observable<StateResource<CommandResource>> {
    return pollWhile(
      command,
      (command: CommandResource): Observable<CommandResource> => this.getCommand(command),
      isPending,
    ).pipe(map((command: CommandResource): StateResource<CommandResource> => this.createCommandStateResource(command)));
  }

  createCommandStateResource(command: CommandResource): StateResource<CommandResource> {
    return createStateResource(command, isPending(command));
  }

  dispatchUpdateIfNotPending(createCommandActionProps: CreateCommandActionProps, update: StateResource<CommandResource>): void {
    if (!isPending(update.resource)) {
      this.dispatchUpdate({ createCommandActionProps, command: update.resource }, update);
    }
  }

  dispatchUpdate(updateProps: UpdateCreateCommandProps, update: StateResource<CommandResource>): void {
    this.store.dispatch(
      hasStateResourceError(update) ?
        CommandActions.createCommandFailure({ ...updateProps, error: update.error })
      : CommandActions.updateCreateCommand({ ...updateProps, command: update.resource }),
    );
  }

  public revokeCommand(resource: CommandResource): Observable<StateResource<CommandResource>> {
    return this.handleCommandResponse(this.repository.revokeCommand(resource));
  }

  private handleCommandResponse(command$: Observable<CommandResource>): Observable<StateResource<CommandResource>> {
    return command$.pipe(
      mergeMap((command) => this.handleCommand(command)),
      catchError((errorResponse) => this.handleHttpError(errorResponse)),
    );
  }

  handleHttpError(errorResponse: HttpErrorResponse): Observable<StateResource<CommandResource>> {
    if (isUnprocessableEntity(errorResponse.status)) {
      return of(createErrorStateResource(errorResponse.error));
    }
    return throwError(() => errorResponse);
  }

  handleCommand(command: CommandResource): Observable<StateResource<CommandResource>> {
    return this.startPolling(command);
  }

  startPolling(commandResource: CommandResource): Observable<StateResource<CommandResource>> {
    return isPending(commandResource) ? this.pollCommand(commandResource) : of(createStateResource(commandResource));
  }

  public pollCommand(commandResource: CommandResource): Observable<StateResource<CommandResource>> {
    return doInIntervall(this.getAndUpdate(commandResource), isNotLoading);
  }

  getAndUpdate(commandResource: CommandResource): Observable<StateResource<CommandResource>> {
    return this.getCommand(commandResource).pipe(
      map((command: CommandResource) => createStateResource(command, isPending(command))),
    );
  }

  getCommand(resource: CommandResource): Observable<CommandResource> {
    if (this.hasCompleteOnSubCommandLink(resource)) {
      return this.repository.getCompleteOnSubCommand(resource);
    } else {
      return this.repository.getCommand(resource);
    }
  }

  private hasCompleteOnSubCommandLink(command: CommandResource): boolean {
    return hasLink(command, CommandLinkRel.COMPLETE_ON_SUB_COMMAND);
  }

  public getEffectedResource<T>(command: CommandResource): Observable<T> {
    return this.repository.getEffectedResource(command);
  }

  _getCommand(createCommandActionProps: CreateCommandActionProps): Observable<StateResource<CommandResource>> {
    return this.store
      .select(
        CommandSelectors.command(
          createCommandActionProps.command.order,
          getUrl(createCommandActionProps.resource, createCommandActionProps.linkRel),
        ),
      )
      .pipe(filter(isNotNil));
  }
}
